/**
 * 游客账号管理
 * 页面加载时自动创建或获取游客账号
 */

(function () {
  "use strict";

  // 配置 - 从全局配置获取 API 基础 URL
  const GUEST_CONFIG = {
    baseUrl: window.THEME_CONFIG?.apiBaseUrl || "https://alb.deliver3d.net:3000",
    storageKey: "deliver3d_guest_token",
    requestTimeoutMs: 10000,
  };

  const isValidToken = (token) =>
    typeof token === "string" &&
    token.trim() !== "" &&
    token !== "undefined" &&
    token !== "null";

  // 游客账号对象
  const GuestAccount = {
    initPromise: null,
    lastInitError: null,

    // 初始化
    async init() {
      if (this.initPromise) {
        return this.initPromise;
      }

      this.initPromise = this.initInternal().finally(() => {
        this.initPromise = null;
      });

      return this.initPromise;
    },

    async initInternal() {
      try {
        // 检查是否已有有效的token
        const existingToken = this.getStoredToken();

        if (existingToken) {
          window.guestToken = existingToken;
          this.lastInitError = null;
          return { token: existingToken };
        }

        // 创建新的游客账号
        const token = await this.retry(() => this.createTourist(), 2, 600);

        if (token) {
          window.guestToken = token;
          this.lastInitError = null;
          return { token };
        }
      } catch (error) {
        this.lastInitError = error;
        this.trackCreationFailed(error);
        return null;
      }
    },

    // 创建游客账号
    async createTourist() {
      const result = await this.runGuestStep(
        "temp_user_auto_login",
        "/auth/tempUserAutoLogin",
        () => this.tempUserAutoLogin(),
      );
      const token = result?.token;
      if (!isValidToken(token)) {
        const error = new Error("No token received from temp user auto login");
        error.guestStep = "temp_user_auto_login";
        error.guestEndpoint = "/auth/tempUserAutoLogin";
        throw error;
      }

      this.storeToken(token);
      return token;
    },

    // API: 创建游客并自动登录
    async tempUserAutoLogin() {
      const response = await this.fetchWithTimeout(
        `${GUEST_CONFIG.baseUrl}/auth/tempUserAutoLogin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Temp user auto login failed: ${response.statusText}`);
      }

      return await response.json();
    },

    async fetchWithTimeout(url, options = {}) {
      if (typeof AbortController === "undefined") {
        return fetch(url, options);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, GUEST_CONFIG.requestTimeoutMs);

      try {
        return await fetch(url, {
          ...options,
          signal: controller.signal,
        });
      } catch (error) {
        if (error && error.name === "AbortError") {
          throw new Error(`Request timeout: ${url}`);
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },

    async runGuestStep(step, endpoint, task) {
      try {
        return await task();
      } catch (error) {
        error.guestStep = error.guestStep || step;
        error.guestEndpoint = error.guestEndpoint || endpoint;
        throw error;
      }
    },

    trackCreationFailed(error) {
      try {
        const guestStep = String(error?.guestStep || this.inferGuestStep(error)).slice(0, 100);
        const params = {
          guest_step: guestStep,
          guest_endpoint: String(error?.guestEndpoint || this.inferGuestEndpoint(error)).slice(0, 200),
          error_type: "guest_auth",
          error_code: guestStep + "_failed",
          has_stored_token: this.getStoredToken() ? "yes" : "no",
        };

        if (window.GtagUtils?.shouldTrack && !window.GtagUtils.shouldTrack("guest_creation_failed_" + guestStep, 1000)) {
          return;
        }

        if (window.GtagUtils && typeof window.GtagUtils.sendEvent === "function") {
          window.GtagUtils.sendEvent("guest_creation_failed", params);
        } else if (typeof window.gtag === "function") {
          window.gtag("event", "guest_creation_failed", params);
        }
      } catch (trackingError) {}
    },

    inferGuestStep(error) {
      const message = String(error?.message || "").toLowerCase();
      if (
        message.includes("/auth/tempuserautologin") ||
        message.includes("temp user auto login")
      ) {
        return "temp_user_auto_login";
      }
      return "unknown";
    },

    inferGuestEndpoint(error) {
      const endpointMap = {
        temp_user_auto_login: "/auth/tempUserAutoLogin",
      };
      return endpointMap[this.inferGuestStep(error)] || "";
    },

    // 从本地存储获取token
    getStoredToken() {
      try {
        const token = localStorage.getItem(GUEST_CONFIG.storageKey);
        if (!isValidToken(token)) {
          localStorage.removeItem(GUEST_CONFIG.storageKey);
          return null;
        }
        return token;
      } catch (error) {
        return null;
      }
    },

    // 存储token到本地
    storeToken(token) {
      if (!isValidToken(token)) return;
      try {
        localStorage.setItem(GUEST_CONFIG.storageKey, token);
      } catch (error) {
        // Silent fail
      }
    },

    // 清除账号
    clearAccount() {
      try {
        localStorage.removeItem(GUEST_CONFIG.storageKey);
        delete window.guestToken;
      } catch (error) {
        // Silent fail
      }
    },

    // 获取Token
    getToken() {
      return isValidToken(window.guestToken) ? window.guestToken : this.getStoredToken();
    },

    async retry(task, retries = 2, delayMs = 600) {
      let lastError = null;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await task();
        } catch (error) {
          lastError = error;
          if (attempt >= retries) break;
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
      throw lastError;
    },
  };

  // 先暴露全局对象，避免自动初始化期间上传模块无法主动等待/复用 init。
  window.GuestAccount = GuestAccount;

  // 页面加载完成后立即初始化
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      GuestAccount.init();
    });
  } else {
    // DOM 已经加载完成
    GuestAccount.init();
  }
})();
